import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Centre de vue globale — SuperAdmin
// Agrège les visites (sites commerçants, pages produits, pages services,
// page d'accueil), les KPI de la plateforme, les sources de trafic, les
// classements, la géolocalisation (pays / villes) et un comparatif avec la
// période précédente (traction investisseurs).
// ─────────────────────────────────────────────────────────────

export interface CommerceOption {
  id: number;
  name: string;
  typeName: string | null;
}

export interface DailyPoint {
  date: string;
  total: number;
}

export interface RankItem {
  id: string;
  title: string;
  sub: string;
  value: number;
  extra: number;
}

export interface SourceItem {
  name: string;
  value: number;
}

export interface RecentVisit {
  id: string;
  kind: string;
  boutique: string;
  ip: string;
  date: string;
  source: string | null;
}

export interface GeoItem {
  name: string;
  code: string | null;
  value: number;
}

export interface DeltaBundle {
  visits: number | null;
  unique: number | null;
  orders: number | null;
  revenue: number | null;
}

export interface ComparisonPoint {
  date: string;
  current: number;
  previous: number;
}

export interface MarketplaceStats {
  annoncesTotal: number;
  annoncesProducts: number;
  annoncesServices: number;
  annoncesPublished: number;
  productItems: number;
  digitalProducts: number;
  servicesItems: number;
}

export interface AccountTypeStat {
  key: string;
  label: string;
  value: number;
}

export interface AcquisitionPoint {
  month: string;
  sites: number;
  accounts: number;
  /** Même mois de l'année précédente (comparatif YoY). */
  sitesLastYear: number;
  accountsLastYear: number;
}

/** Série quotidienne d'une source de trafic (Google, WhatsApp…). */
export interface SourceEvolutionPoint {
  date: string;
  total: number;
  [source: string]: number | string;
}

export type AccountTypeKey = 'Boutique' | 'Services' | 'Mixte' | 'Restauration' | 'Autres';

/** Normalise le libellé du type de compte en une catégorie lisible. */
export function accountBucket(label: string): AccountTypeKey {
  const lower = (label || '').toLowerCase();
  if (lower.includes('mixte')) return 'Mixte';
  if (lower.includes('restaur')) return 'Restauration';
  if (lower.includes('service')) return 'Services';
  if (lower.includes('boutique')) return 'Boutique';
  return 'Autres';
}

export interface TractionStats {
  growth7d: number | null;
  growth30d: number | null;
  returningRate: number | null;
  returningVisitors: number;
  totalVisitors: number;
  views7d: number;
  views30d: number;
}

export interface SuperStats {
  visitsAllTime: number;
  uniqueAllTime: number;
  visitsPeriod: number;
  uniquePeriod: number;
  landingVisits: number;
  siteVisits: number;
  productVisits: number;
  serviceVisits: number;
  /** Pages publiques de contenu également suivies via `theme_page_visits`. */
  bookingVisits: number;
  formVisits: number;
  hebergementVisits: number;
  /** Réservations créées sur la période (rendez-vous). */
  bookings: number;
  /** Taux de conversion fiche service → réservation (%). null si aucune vue. */
  serviceConversion: number | null;
  siteUsers: number;
  sites: number;
  products: number;
  services: number;
  orders: number;
  revenue: number;
  currency: string;
  visitsByDay: DailyPoint[];
  sources: SourceItem[];
  topSites: RankItem[];
  topProducts: RankItem[];
  /** Classement des produits filtré par source de trafic (clé = libellé de source). */
  topProductsBySource: Record<string, RankItem[]>;
  /** Sources ayant apporté des vues produit (pour le filtre). */
  productSources: SourceItem[];
  topServices: RankItem[];
  /** Classement des services filtré par source de trafic (clé = libellé de source). */
  topServicesBySource: Record<string, RankItem[]>;
  /** Sources ayant apporté des vues service (pour le filtre). */
  serviceSources: SourceItem[];
  /** Taux de conversion fiche produit → commande (%). null si aucune vue. */
  productConversion: number | null;
  recentVisits: RecentVisit[];
  geoCountries: GeoItem[];
  geoCities: GeoItem[];
  geoTotal: number;
  geoPending: number;
  comparisonByDay: ComparisonPoint[];
  marketplace: MarketplaceStats;
  accountTypes: AccountTypeStat[];
  acquisition: AcquisitionPoint[];
  sourceEvolution: SourceEvolutionPoint[];
  traction: TractionStats;
  deltas: DeltaBundle;
}

interface Params {
  startDate: string;
  endDate: string;
  scopeId: number;
  accountType?: AccountTypeKey | null;
}

interface VisitRow {
  id: number;
  ip: string | null;
  date: string | null;
  source: string | null;
  boutique_id?: number | null;
  boutique_nom?: string | null;
  boutique?: string | null;
  id_produit?: number | null;
}

interface OrderRow {
  subtotal_items: number | null;
  shipping_total: number | null;
  tax_total: number | null;
  discount_price: number | null;
  currency: string | null;
  created_at: string | null;
  status: string | null;
}

interface GeoBundle {
  countries: GeoItem[];
  cities: GeoItem[];
  total: number;
  pending: number;
}

const EMPTY_STATS: SuperStats = {
  visitsAllTime: 0,
  uniqueAllTime: 0,
  visitsPeriod: 0,
  uniquePeriod: 0,
  landingVisits: 0,
  siteVisits: 0,
  productVisits: 0,
  serviceVisits: 0,
  bookingVisits: 0,
  formVisits: 0,
  hebergementVisits: 0,
  bookings: 0,
  serviceConversion: null,
  productConversion: null,
  siteUsers: 0,
  sites: 0,
  products: 0,
  services: 0,
  orders: 0,
  revenue: 0,
  currency: 'MAD',
  visitsByDay: [],
  sources: [],
  topSites: [],
  topProducts: [],
  topProductsBySource: {},
  productSources: [],
  topServices: [],
  topServicesBySource: {},
  serviceSources: [],
  recentVisits: [],
  geoCountries: [],
  geoCities: [],
  geoTotal: 0,
  geoPending: 0,
  comparisonByDay: [],
  marketplace: {
    annoncesTotal: 0,
    annoncesProducts: 0,
    annoncesServices: 0,
    annoncesPublished: 0,
    productItems: 0,
    digitalProducts: 0,
    servicesItems: 0,
  },
  accountTypes: [],
  acquisition: [],
  sourceEvolution: [],
  traction: {
    growth7d: null,
    growth30d: null,
    returningRate: null,
    returningVisitors: 0,
    totalVisitors: 0,
    views7d: 0,
    views30d: 0,
  },
  deltas: { visits: null, unique: null, orders: null, revenue: null },
};

const KIND_BY_TABLE: Record<string, string> = {
  visitesiteweb: 'Site web',
  visiteproduitsiteweb: 'Page produit',
  visiteservicesiteweb: 'Page service',
  visitelandingpage: 'Accueil Zifek',
};

// Garde-fou : l'enrichissement géo (appel à l'Edge Function) n'est tenté
// qu'une seule fois par session navigateur.
let geoEnrichAttempted = false;

function dayKey(value: string | null | undefined): string {
  if (!value) return '';
  return value.split(/[T ]/)[0];
}

function maskIp(ip: string | null | undefined): string {
  if (!ip) return 'Non géolocalisée';
  const parts = ip.split('.');
  if (parts.length === 4) return `${parts[0]}.${parts[1]}.•••.•••`;
  return ip.length > 6 ? `${ip.slice(0, 6)}…` : ip;
}

/**
 * Nature d'une visite de page publique à partir de son chemin. Les fiches
 * produits / services / annonces passent toutes par le suivi de thème
 * (`theme_page_visits`) : c'est le CHEMIN qui distingue la nature réelle de la
 * page (une fiche /service/:id est un service, /product/:id un produit, etc.).
 */
type VisitKind = 'product' | 'service' | 'booking' | 'form' | 'hebergement' | 'other';

function parseVisitKind(path: string | null | undefined): { kind: VisitKind; id: number | null } {
  const p = (path || '').toLowerCase().split('?')[0];
  const m = p.match(/^\/(product|produit|service|annonce|hebergement|booking|forms)(?:\/(\d+))?/);
  if (!m) return { kind: 'other', id: null };
  const seg = m[1];
  const id = m[2] ? Number(m[2]) : null;
  if (seg === 'product' || seg === 'produit' || seg === 'annonce') return { kind: 'product', id };
  if (seg === 'service') return { kind: 'service', id };
  if (seg === 'hebergement') return { kind: 'hebergement', id };
  if (seg === 'booking') return { kind: 'booking', id };
  if (seg === 'forms') return { kind: 'form', id };
  return { kind: 'other', id: null };
}

function toDateInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildDays(startDate: string, endDate: string, totals: Map<string, number>): DailyPoint[] {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const days: DailyPoint[] = [];
  const cursor = new Date(start);
  let guard = 0;
  while (cursor <= end && guard < 400) {
    const key = toDateInput(cursor);
    days.push({
      date: cursor.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
      total: totals.get(key) || 0,
    });
    cursor.setDate(cursor.getDate() + 1);
    guard += 1;
  }
  return days;
}

/** Période précédente de même longueur, immédiatement avant la période courante. */
function previousPeriod(startDate: string, endDate: string): { start: string; end: string } {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
  const prevEnd = new Date(start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (days - 1));
  return { start: toDateInput(prevStart), end: toDateInput(prevEnd) };
}

/** Variation en pourcentage. null si aucune base de comparaison. */
function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
}

async function headCount(table: string, siteIds: number[] | null = null): Promise<number> {
  let query = supabase.from(table).select('id', { count: 'exact', head: true });
  if (siteIds && siteIds.length > 0 && table !== 'visitelandingpage') {
    query = query.in('boutique_id', siteIds);
  }
  const { count, error } = await query;
  if (error) return 0;
  return count || 0;
}

/**
 * Nombre de services publiés par les sites de la plateforme. Les services
 * marchands vivent dans `nospartenairesservices` (et non dans `services`, qui
 * est la table legacy Zifek restée vide). Filtre optionnel par site.
 */
async function fetchServicesCount(siteIds: number[] | null = null): Promise<number> {
  let query = supabase
    .from('nospartenairesservices')
    .select('id', { count: 'exact', head: true });
  if (siteIds && siteIds.length > 0) query = query.in('idcommerce', siteIds);
  const { count, error } = await query;
  if (error) return 0;
  return count || 0;
}

async function fetchRows(
  table: string,
  siteIds: number[] | null,
  startIso: string | null,
  endIso: string | null,
): Promise<VisitRow[]> {
  let query = supabase.from(table).select('*');
  if (startIso && endIso) {
    query = query.gte('date', startIso).lte('date', endIso);
  }
  if (siteIds && siteIds.length > 0 && table !== 'visitelandingpage') {
    query = query.in('boutique_id', siteIds);
  }
  const { data, error } = await query.limit(50000);
  if (error) throw error;
  return (data || []) as VisitRow[];
}

async function fetchIps(table: string, siteIds: number[] | null): Promise<string[]> {
  let query = supabase.from(table).select('ip');
  if (siteIds && siteIds.length > 0 && table !== 'visitelandingpage') {
    query = query.in('boutique_id', siteIds);
  }
  const { data, error } = await query.limit(100000);
  if (error) return [];
  return ((data || []) as { ip: string | null }[])
    .map((row) => row.ip)
    .filter((ip): ip is string => Boolean(ip && ip.trim()));
}

/** Agrège la géolocalisation réelle (table theme_page_visits) sur la période. */
async function loadGeo(siteIds: number[] | null, startIso: string, endIso: string): Promise<GeoBundle> {
  let query = supabase
    .from('theme_page_visits')
    .select('ip, country, city, country_code, event, created_at')
    .gte('created_at', startIso)
    .lte('created_at', endIso);
  if (siteIds && siteIds.length > 0) query = query.in('boutique_id', siteIds);
  const { data, error } = await query.limit(50000);
  if (error) return { countries: [], cities: [], total: 0, pending: 0 };

  const rows = (data || []) as {
    ip: string | null;
    country: string | null;
    city: string | null;
    country_code: string | null;
    event: string | null;
  }[];
  const views = rows.filter((r) => (r.event || 'view') === 'view');

  const countryMap = new Map<string, { code: string | null; value: number }>();
  const cityMap = new Map<string, number>();
  let total = 0;
  let pending = 0;

  views.forEach((row) => {
    const country = (row.country || '').trim();
    const city = (row.city || '').trim();
    if (country) {
      total += 1;
      const entry = countryMap.get(country) || { code: row.country_code || null, value: 0 };
      entry.value += 1;
      countryMap.set(country, entry);
      if (city) cityMap.set(city, (cityMap.get(city) || 0) + 1);
    } else if (row.ip && row.ip.trim()) {
      pending += 1;
    }
  });

  const countries: GeoItem[] = Array.from(countryMap.entries())
    .map(([name, entry]) => ({ name, code: entry.code, value: entry.value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 12);
  const cities: GeoItem[] = Array.from(cityMap.entries())
    .map(([name, value]) => ({ name, code: null, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  return { countries, cities, total, pending };
}

interface ThemeVisitRow {
  ip: string | null;
  boutique_id: number | null;
  source: string | null;
  event: string | null;
  created_at: string | null;
  path: string | null;
}

/**
 * Visites réelles des sites publics (table `theme_page_visits`). C'est la
 * SEULE source fiable pour les visiteurs uniques : les tables historiques
 * `visitesiteweb` / `visitelandingpage` enregistrent une colonne `ip` vide,
 * ce qui donnait « 0 visiteur unique » alors que les visites existaient.
 */
async function loadThemeVisits(
  siteIds: number[] | null,
  startIso: string | null,
  endIso: string | null,
): Promise<ThemeVisitRow[]> {
  let query = supabase
    .from('theme_page_visits')
    .select('ip, boutique_id, source, event, created_at, path');
  if (startIso && endIso) query = query.gte('created_at', startIso).lte('created_at', endIso);
  if (siteIds && siteIds.length > 0) query = query.in('boutique_id', siteIds);
  const { data, error } = await query.limit(100000);
  if (error) return [];
  return (data || []) as ThemeVisitRow[];
}

/** Uniformise le libellé de provenance (« direct » → « Direct / inconnu »). */
function normalizeSource(label: string | null | undefined): string {
  const v = (label || '').trim();
  if (!v) return 'Direct / inconnu';
  const low = v.toLowerCase();
  if (low === 'direct' || low === '(direct)' || low === 'direct / inconnu') return 'Direct / inconnu';
  return v;
}

/** Traction réelle : croissance 7 j / 30 j et taux de retour visiteurs. */
async function loadTraction(siteIds: number[] | null): Promise<TractionStats> {
  const now = new Date();
  const since = new Date(now);
  since.setDate(since.getDate() - 60);
  const sinceIso = `${toDateInput(since)}T00:00:00`;

  let query = supabase
    .from('theme_page_visits')
    .select('ip, event, created_at')
    .gte('created_at', sinceIso);
  if (siteIds && siteIds.length > 0) query = query.in('boutique_id', siteIds);

  const { data, error } = await query.limit(100000);
  if (error) return EMPTY_STATS.traction;

  const rows = ((data || []) as { ip: string | null; event: string | null; created_at: string }[])
    .filter((r) => (r.event || 'view') === 'view');

  const dayMs = 86400000;
  const startOfToday = new Date(`${toDateInput(now)}T00:00:00`).getTime();
  let views7d = 0;
  let prevViews7d = 0;
  let views30d = 0;
  let prevViews30d = 0;
  const ipCounts = new Map<string, number>();

  rows.forEach((row) => {
    const raw = row.created_at || '';
    const t = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T')).getTime();
    if (Number.isNaN(t)) return;
    const daysAgo = Math.floor((startOfToday - t) / dayMs);
    if (daysAgo < 7) views7d += 1;
    else if (daysAgo < 14) prevViews7d += 1;
    if (daysAgo < 30) views30d += 1;
    else if (daysAgo < 60) prevViews30d += 1;
    const ip = (row.ip || '').trim();
    if (ip) ipCounts.set(ip, (ipCounts.get(ip) || 0) + 1);
  });

  const totalVisitors = ipCounts.size;
  let returningVisitors = 0;
  ipCounts.forEach((count) => {
    if (count >= 2) returningVisitors += 1;
  });

  return {
    views7d,
    views30d,
    growth7d: pctChange(views7d, prevViews7d),
    growth30d: pctChange(views30d, prevViews30d),
    returningVisitors,
    totalVisitors,
    returningRate: totalVisitors > 0 ? (returningVisitors / totalVisitors) * 100 : null,
  };
}

export function useSuperStats(params: Params, commerces: CommerceOption[]) {
  const { startDate, endDate, scopeId, accountType } = params;
  const [stats, setStats] = useState<SuperStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const enrichRef = useRef(false);

  const commerceNameById = new Map<number, string>();
  commerces.forEach((c) => {
    commerceNameById.set(c.id, c.name);
  });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const startIso = `${startDate}T00:00:00`;
      const endIso = `${endDate}T23:59:59.999`;

      // ── KPI « depuis la création » ─────────────────────────
      // ── Contenus de la plateforme ──────────────────────────
      const [prodCount, siteUsersRes, sitesRes, clientTypesRes, typeNamesRes] = await Promise.all([
        headCount('product_items'),
        supabase.from('userwebsite').select('idcommerce').limit(50000),
        supabase.from('users').select('id, typecompte, datecreation').limit(50000),
        supabase.from('typecompte').select('id').eq('statustypecompte', 1),
        supabase.from('typecompte').select('id, nom'),
      ]);

      const clientTypeIds = new Set<number>(
        ((clientTypesRes.data || []) as { id: number }[]).map((t) => t.id),
      );
      const shopUsers = ((sitesRes.data || []) as { id: number; typecompte: number | null; datecreation: string | null }[])
        .filter((u) => u.typecompte == null || !clientTypeIds.has(u.typecompte));

      const userWebsiteRows = (siteUsersRes.data || []) as { idcommerce: number | null }[];
      const usersByCommerce = new Map<number, number>();
      userWebsiteRows.forEach((row) => {
        if (typeof row.idcommerce === 'number') {
          usersByCommerce.set(row.idcommerce, (usersByCommerce.get(row.idcommerce) || 0) + 1);
        }
      });

      // ── Répartition par type de compte (sites) ─────────────
      const typeNameById = new Map<number, string>();
      ((typeNamesRes.data || []) as { id: number; nom: string | null }[]).forEach((t) => {
        typeNameById.set(t.id, t.nom || '');
      });
      const bucketCounts: Record<AccountTypeKey, number> = {
        Boutique: 0, Services: 0, Mixte: 0, Restauration: 0, Autres: 0,
      };
      shopUsers.forEach((u) => {
        const label = u.typecompte != null ? typeNameById.get(u.typecompte) || '' : '';
        bucketCounts[accountBucket(label)] += 1;
      });
      const accountTypes: AccountTypeStat[] = [
        { key: 'Boutique', label: 'Boutique', value: bucketCounts.Boutique },
        { key: 'Services', label: 'Services', value: bucketCounts.Services },
        { key: 'Mixte', label: 'Mixte', value: bucketCounts.Mixte },
        { key: 'Restauration', label: 'Restauration', value: bucketCounts.Restauration },
        { key: 'Autres', label: 'Autres', value: bucketCounts.Autres },
      ];

      // ── Filtre par type de compte → ensemble de sites concernés ──
      const typeFilteredIds = accountType
        ? shopUsers
            .filter((u) => accountBucket(u.typecompte != null ? typeNameById.get(u.typecompte) || '' : '') === accountType)
            .map((u) => u.id)
        : null;
      const siteIds: number[] | null = accountType
        ? (typeFilteredIds && typeFilteredIds.length > 0 ? typeFilteredIds : [-1])
        : (scopeId > 0 ? [scopeId] : null);
      const sitesCount = accountType ? (typeFilteredIds ? typeFilteredIds.length : 0) : shopUsers.length;

      // ── KPI « depuis la création » + services ─────────────
      const [servCount, siteAll, prodAll, servAll, landAll] = await Promise.all([
        fetchServicesCount(siteIds),
        headCount('visitesiteweb', siteIds),
        headCount('visiteproduitsiteweb', siteIds),
        headCount('visiteservicesiteweb', siteIds),
        headCount('visitelandingpage', null),
      ]);

      // ── Acquisition : nouveaux sites / comptes par mois (24 mois
      //    pour permettre le comparatif avec l'année précédente) ────
      const monthKeys: { key: string; label: string }[] = [];
      const cap = new Date();
      for (let i = 23; i >= 0; i -= 1) {
        const d = new Date(cap.getFullYear(), cap.getMonth() - i, 1);
        monthKeys.push({
          key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
          label: d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
        });
      }
      const allUsersRows = ((sitesRes.data || []) as { id: number; typecompte: number | null; datecreation: string | null }[]);
      const siteIdSet = new Set(shopUsers.map((u) => u.id));
      const accountByMonth = new Map<string, number>();
      const siteByMonth = new Map<string, number>();
      const monthOf = (value: string | null): string => {
        if (!value) return '';
        const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
        if (Number.isNaN(d.getTime())) return '';
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      };
      allUsersRows.forEach((u) => {
        const m = monthOf(u.datecreation);
        if (!m) return;
        accountByMonth.set(m, (accountByMonth.get(m) || 0) + 1);
        if (siteIdSet.has(u.id)) siteByMonth.set(m, (siteByMonth.get(m) || 0) + 1);
      });
      const acquisitionAll: AcquisitionPoint[] = monthKeys.map((mk) => ({
        month: mk.label,
        accounts: accountByMonth.get(mk.key) || 0,
        sites: siteByMonth.get(mk.key) || 0,
        sitesLastYear: 0,
        accountsLastYear: 0,
      }));
      // On affiche les 12 derniers mois ; « lastYear » = même mois un an avant
      // (index aligné dans la fenêtre de 24 mois).
      const acquisition: AcquisitionPoint[] = acquisitionAll.slice(-12).map((point, idx) => ({
        ...point,
        sitesLastYear: acquisitionAll[idx]?.sites ?? 0,
        accountsLastYear: acquisitionAll[idx]?.accounts ?? 0,
      }));

      const prodItemsRes = await supabase.from('product_items').select('id, name, product_type').limit(50000);
      let servItemsQuery = supabase.from('nospartenairesservices').select('id, titre');
      if (siteIds && siteIds.length > 0) servItemsQuery = servItemsQuery.in('idcommerce', siteIds);
      const servItemsRes = await servItemsQuery.limit(50000);
      const productNameById = new Map<number, string>();
      ((prodItemsRes.data || []) as { id: number; name: string | null }[]).forEach((p) => {
        productNameById.set(p.id, p.name || `Produit #${p.id}`);
      });
      const serviceNameById = new Map<number, string>();
      ((servItemsRes.data || []) as { id: number; titre: string | null }[]).forEach((s) => {
        serviceNameById.set(s.id, s.titre || `Service #${s.id}`);
      });

      // ── Contenus & publications (marketplace / boutique) ───
      const annoncesRes = await supabase.from('annonce').select('id, titre, type, created').limit(50000);
      const annonceRows = (annoncesRes.data || []) as {
        id: number;
        titre: string | null;
        type: string | null;
        created: string | null;
      }[];
      // Résolution des noms de contenus marketplace (produits/services de la
      // table `annonce`) pour les classements « les plus visités ».
      const annonceNameById = new Map<number, string>();
      annonceRows.forEach((a) => {
        annonceNameById.set(a.id, a.titre || `Contenu #${a.id}`);
      });
      const digitalProducts = ((prodItemsRes.data || []) as { product_type?: string | null }[])
        .filter((p) => p.product_type === 'digital').length;
      const marketplace: MarketplaceStats = {
        annoncesTotal: annonceRows.length,
        annoncesProducts: annonceRows.filter((a) => a.type === 'produits').length,
        annoncesServices: annonceRows.filter((a) => a.type === 'services').length,
        annoncesPublished: annonceRows.filter((a) => {
          const key = dayKey(a.created);
          return key >= startDate && key <= endDate;
        }).length,
        productItems: prodCount,
        digitalProducts,
        servicesItems: servCount,
      };

      // ── Visites sur la période ─────────────────────────────
      const [siteRows, prodRows, servRows, landRows] = await Promise.all([
        fetchRows('visitesiteweb', siteIds, startIso, endIso),
        fetchRows('visiteproduitsiteweb', siteIds, startIso, endIso),
        fetchRows('visiteservicesiteweb', siteIds, startIso, endIso),
        fetchRows('visitelandingpage', null, startIso, endIso),
      ]);

      const allRows: VisitRow[] = [
        ...siteRows.map((r) => ({ ...r, __kind: 'visitesiteweb' } as VisitRow & { __kind: string })),
        ...prodRows.map((r) => ({ ...r, __kind: 'visiteproduitsiteweb' } as VisitRow & { __kind: string })),
        ...servRows.map((r) => ({ ...r, __kind: 'visiteservicesiteweb' } as VisitRow & { __kind: string })),
        ...landRows.map((r) => ({ ...r, __kind: 'visitelandingpage' } as VisitRow & { __kind: string })),
      ];

      // ── Suivi de thème réel (IP + provenance) ──────────────
      // Source fiable des visiteurs uniques : les tables historiques ont une
      // colonne `ip` vide ; `theme_page_visits` porte les vraies IP.
      const [themePeriodRows, themeAllRows] = await Promise.all([
        loadThemeVisits(siteIds, startIso, endIso),
        loadThemeVisits(siteIds, null, null),
      ]);
      const themePeriodViews = themePeriodRows.filter((r) => (r.event || 'view') === 'view');

      // ── Visites des pages de contenu (produit / service / réservation /
      //    formulaire / hébergement) depuis le suivi de thème ──
      // C'est LA source fiable : les tables historiques legacy sont restées
      // vides alors que les vues réelles sont enregistrées ici.
      const themeKindCount: Record<VisitKind, number> = {
        product: 0, service: 0, booking: 0, form: 0, hebergement: 0, other: 0,
      };
      const themeProductAgg = new Map<number, { total: number; ips: Set<string> }>();
      const themeServiceAgg = new Map<number, { total: number; ips: Set<string> }>();
      // Regroupement PAR SOURCE (Google / WhatsApp / Instagram…) pour pouvoir
      // filtrer le classement des produits ET des services les plus visités.
      const themeProductBySource = new Map<string, Map<number, { total: number; ips: Set<string> }>>();
      const themeServiceBySource = new Map<string, Map<number, { total: number; ips: Set<string> }>>();
      themePeriodViews.forEach((r) => {
        const { kind, id } = parseVisitKind(r.path);
        themeKindCount[kind] += 1;
        if (id == null) return;
        const ip = (r.ip || '').trim();
        if (kind === 'product') {
          const e = themeProductAgg.get(id) || { total: 0, ips: new Set<string>() };
          e.total += 1;
          if (ip) e.ips.add(ip);
          themeProductAgg.set(id, e);
          const src = normalizeSource(r.source);
          if (!themeProductBySource.has(src)) themeProductBySource.set(src, new Map());
          const bySrc = themeProductBySource.get(src)!;
          const se = bySrc.get(id) || { total: 0, ips: new Set<string>() };
          se.total += 1;
          if (ip) se.ips.add(ip);
          bySrc.set(id, se);
        } else if (kind === 'service') {
          const e = themeServiceAgg.get(id) || { total: 0, ips: new Set<string>() };
          e.total += 1;
          if (ip) e.ips.add(ip);
          themeServiceAgg.set(id, e);
          const src = normalizeSource(r.source);
          if (!themeServiceBySource.has(src)) themeServiceBySource.set(src, new Map());
          const bySrc = themeServiceBySource.get(src)!;
          const se = bySrc.get(id) || { total: 0, ips: new Set<string>() };
          se.total += 1;
          if (ip) se.ips.add(ip);
          bySrc.set(id, se);
        }
      });

      const themePeriodIpsBySite = new Map<number, Set<string>>();
      themePeriodViews.forEach((r) => {
        const ip = (r.ip || '').trim();
        if (!ip || r.boutique_id == null) return;
        if (!themePeriodIpsBySite.has(r.boutique_id)) themePeriodIpsBySite.set(r.boutique_id, new Set());
        themePeriodIpsBySite.get(r.boutique_id)!.add(ip);
      });

      const periodIps = new Set<string>();
      allRows.forEach((row) => {
        if (row.ip && row.ip.trim()) periodIps.add(row.ip.trim());
      });
      themePeriodViews.forEach((row) => {
        const ip = (row.ip || '').trim();
        if (ip) periodIps.add(ip);
      });

      // ── Visiteurs uniques depuis la création ───────────────
      const ipGroups = await Promise.all([
        fetchIps('visitesiteweb', siteIds),
        fetchIps('visiteproduitsiteweb', siteIds),
        fetchIps('visiteservicesiteweb', siteIds),
        fetchIps('visitelandingpage', null),
      ]);
      const allIps = new Set<string>();
      ipGroups.forEach((list) => list.forEach((ip) => allIps.add(ip)));
      themeAllRows.forEach((row) => {
        if ((row.event || 'view') !== 'view') return;
        const ip = (row.ip || '').trim();
        if (ip) allIps.add(ip);
      });

      // ── Commandes de la période ────────────────────────────
      const ordersRes = await supabase
        .from('order_headers')
        .select('subtotal_items, shipping_total, tax_total, discount_price, currency, created_at, status')
        .gte('created_at', startIso)
        .lte('created_at', endIso)
        .limit(50000);
      const orderRows = (ordersRes.data || []) as OrderRow[];
      let revenue = 0;
      let currency = 'MAD';
      orderRows.forEach((order) => {
        revenue +=
          (order.subtotal_items || 0)
          + (order.shipping_total || 0)
          + (order.tax_total || 0)
          - (order.discount_price || 0);
        if (order.currency) currency = order.currency;
      });

      // ── Réservations créées sur la période (conversion fiche service → RDV) ──
      let bookings = 0;
      try {
        let bookingServiceIds: number[] | null = null;
        if (siteIds && siteIds.length > 0) {
          const { data: svcOwners } = await supabase
            .from('booking_services')
            .select('id')
            .in('owner', siteIds);
          bookingServiceIds = ((svcOwners || []) as { id: number }[]).map((r) => r.id);
        }
        let bQuery = supabase
          .from('booking_appointments')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', startIso)
          .lte('created_at', endIso);
        if (bookingServiceIds) {
          bQuery = bookingServiceIds.length > 0
            ? bQuery.in('service_id', bookingServiceIds)
            : bQuery.eq('service_id', -1);
        }
        const { count } = await bQuery;
        bookings = count || 0;
      } catch { bookings = 0; }

      // ── Comparatif période précédente (traction) ───────────
      const prev = previousPeriod(startDate, endDate);
      const prevStartIso = `${prev.start}T00:00:00`;
      const prevEndIso = `${prev.end}T23:59:59.999`;
      const [pSite, pProd, pServ, pLand] = await Promise.all([
        fetchRows('visitesiteweb', siteIds, prevStartIso, prevEndIso),
        fetchRows('visiteproduitsiteweb', siteIds, prevStartIso, prevEndIso),
        fetchRows('visiteservicesiteweb', siteIds, prevStartIso, prevEndIso),
        fetchRows('visitelandingpage', null, prevStartIso, prevEndIso),
      ]);
      const prevAll = [...pSite, ...pProd, ...pServ, ...pLand];
      const prevVisits = prevAll.length;
      const prevIps = new Set<string>();
      prevAll.forEach((row) => {
        if (row.ip && row.ip.trim()) prevIps.add(row.ip.trim());
      });
      const prevOrdersRes = await supabase
        .from('order_headers')
        .select('subtotal_items, shipping_total, tax_total, discount_price, currency, created_at, status')
        .gte('created_at', prevStartIso)
        .lte('created_at', prevEndIso)
        .limit(50000);
      const prevOrderRows = (prevOrdersRes.data || []) as OrderRow[];
      let prevRevenue = 0;
      prevOrderRows.forEach((order) => {
        prevRevenue +=
          (order.subtotal_items || 0)
          + (order.shipping_total || 0)
          + (order.tax_total || 0)
          - (order.discount_price || 0);
      });

      // ── Évolution quotidienne ──────────────────────────────
      const dayTotals = new Map<string, number>();
      allRows.forEach((row) => {
        const key = dayKey(row.date);
        if (!key) return;
        dayTotals.set(key, (dayTotals.get(key) || 0) + 1);
      });
      const visitsByDay = buildDays(startDate, endDate, dayTotals);

      // ── Comparatif superposé : période actuelle vs précédente ──
      const prevDayTotals = new Map<string, number>();
      prevAll.forEach((row) => {
        const key = dayKey(row.date);
        if (!key) return;
        prevDayTotals.set(key, (prevDayTotals.get(key) || 0) + 1);
      });
      const prevDays = buildDays(prev.start, prev.end, prevDayTotals);
      const comparisonByDay: ComparisonPoint[] = visitsByDay.map((point, index) => ({
        date: point.date,
        current: point.total,
        previous: prevDays[index]?.total ?? 0,
      }));

      // ── Sources de trafic ──────────────────────────────────
      // Provenance réelle : suivi de thème (referrer / UTM / navigateur
      // in-app → Google, WhatsApp, Instagram…) fusionné avec les sources
      // historiques des tables de visites (normalisées).
      const sourceMap = new Map<string, number>();
      const addSource = (label: string | null | undefined) => {
        const key = normalizeSource(label);
        sourceMap.set(key, (sourceMap.get(key) || 0) + 1);
      };
      themePeriodViews.forEach((row) => addSource(row.source));
      allRows.forEach((row) => addSource(row.source));
      const sources: SourceItem[] = Array.from(sourceMap.entries())
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);

      // ── Évolution des sources dans le temps ──
      // Série quotidienne par provenance (Google, WhatsApp, Instagram…), pour
      // visualiser concrètement l'arrivée de chaque canal au fil des jours.
      const topSourceKeys = sources.slice(0, 5).map((s) => s.name);
      const evolutionColumns = [...topSourceKeys, 'Autres'];
      const evolutionKeys: string[] = [];
      {
        const cursor = new Date(`${startDate}T00:00:00`);
        const endD = new Date(`${endDate}T00:00:00`);
        let guard = 0;
        while (cursor <= endD && guard < 400) {
          evolutionKeys.push(toDateInput(cursor));
          cursor.setDate(cursor.getDate() + 1);
          guard += 1;
        }
      }
      const byDaySource = new Map<string, Record<string, number>>();
      themePeriodViews.forEach((row) => {
        const key = dayKey(row.created_at);
        if (!key) return;
        const src = normalizeSource(row.source);
        const bucket = topSourceKeys.includes(src) ? src : 'Autres';
        const rec = byDaySource.get(key) || {};
        rec[bucket] = (rec[bucket] || 0) + 1;
        byDaySource.set(key, rec);
      });
      const sourceEvolution: SourceEvolutionPoint[] = evolutionKeys.map((key) => {
        const rec = byDaySource.get(key) || {};
        const point: SourceEvolutionPoint = {
          date: new Date(`${key}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        };
        let total = 0;
        evolutionColumns.forEach((name) => {
          const v = rec[name] || 0;
          point[name] = v;
          total += v;
        });
        point.total = total;
        return point;
      });

      // ── Top sites visités ──────────────────────────────────
      const siteAgg = new Map<number, { total: number; ips: Set<string>; name: string }>();
      siteRows.forEach((row) => {
        const id = row.boutique_id ?? 0;
        if (!siteAgg.has(id)) {
          siteAgg.set(id, {
            total: 0,
            ips: new Set<string>(),
            name: row.boutique_nom || commerceNameById.get(id) || `Site #${id}`,
          });
        }
        const entry = siteAgg.get(id)!;
        entry.total += 1;
        if (row.ip) entry.ips.add(row.ip);
      });
      const topSites: RankItem[] = Array.from(siteAgg.entries())
        .map(([id, data]) => {
          const legacyUnique = data.ips.size;
          const themeUnique = themePeriodIpsBySite.get(id)?.size || 0;
          const uniqueCount = Math.min(Math.max(legacyUnique, themeUnique), data.total);
          return {
            id: String(id),
            title: data.name,
            sub: `${uniqueCount} visiteur(s) unique(s)`,
            value: data.total,
            extra: usersByCommerce.get(id) || 0,
          };
        })
        .sort((a, b) => b.value - a.value)
        .slice(0, 12);

      // ── Top produits visités ───────────────────────────────
      const prodAgg = new Map<number, { total: number; ips: Set<string>; name: string; site: string }>();
      prodRows.forEach((row) => {
        const id = row.id_produit ?? 0;
        if (!prodAgg.has(id)) {
          prodAgg.set(id, {
            total: 0,
            ips: new Set<string>(),
            name: productNameById.get(id) || `Produit #${id}`,
            site: row.boutique || commerceNameById.get(row.boutique_id ?? 0) || '',
          });
        }
        const entry = prodAgg.get(id)!;
        entry.total += 1;
        if (row.ip) entry.ips.add(row.ip);
      });
      // Fusion avec le suivi de thème (source réelle des vues des fiches produit).
      themeProductAgg.forEach((data, id) => {
        const entry = prodAgg.get(id) || {
          total: 0,
          ips: new Set<string>(),
          name: productNameById.get(id) || annonceNameById.get(id) || `Produit #${id}`,
          site: '',
        };
        entry.total += data.total;
        data.ips.forEach((ip) => entry.ips.add(ip));
        prodAgg.set(id, entry);
      });
      const topProducts: RankItem[] = Array.from(prodAgg.entries())
        .map(([id, data]) => ({
          id: String(id),
          title: data.name,
          sub: `${data.ips.size} unique(s)`,
          value: data.total,
          extra: 0,
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 12);

      // ── Produits les plus visités PAR SOURCE (filtre Google / WhatsApp…) ──
      const productSources: SourceItem[] = Array.from(themeProductBySource.keys())
        .map((name) => ({
          name,
          value: Array.from(themeProductBySource.get(name)!.values())
            .reduce((sum, e) => sum + e.total, 0),
        }))
        .sort((a, b) => b.value - a.value);
      const topProductsBySource: Record<string, RankItem[]> = {};
      themeProductBySource.forEach((bySrc, srcName) => {
        topProductsBySource[srcName] = Array.from(bySrc.entries())
          .map(([id, data]) => ({
            id: String(id),
            title: productNameById.get(id) || annonceNameById.get(id) || `Produit #${id}`,
            sub: `${data.ips.size} unique(s)`,
            value: data.total,
            extra: 0,
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 12);
      });

      // ── Top services visités ───────────────────────────────
      const servAgg = new Map<number, { total: number; ips: Set<string>; name: string }>();
      servRows.forEach((row) => {
        const id = row.id_produit ?? 0;
        if (!servAgg.has(id)) {
          servAgg.set(id, {
            total: 0,
            ips: new Set<string>(),
            name: serviceNameById.get(id) || `Service #${id}`,
          });
        }
        const entry = servAgg.get(id)!;
        entry.total += 1;
        if (row.ip) entry.ips.add(row.ip);
      });
      // Fusion avec le suivi de thème (source réelle des vues des fiches service).
      themeServiceAgg.forEach((data, id) => {
        const entry = servAgg.get(id) || {
          total: 0,
          ips: new Set<string>(),
          name: serviceNameById.get(id) || annonceNameById.get(id) || `Service #${id}`,
        };
        entry.total += data.total;
        data.ips.forEach((ip) => entry.ips.add(ip));
        servAgg.set(id, entry);
      });
      const topServices: RankItem[] = Array.from(servAgg.entries())
        .map(([id, data]) => ({
          id: String(id),
          title: data.name,
          sub: `${data.ips.size} unique(s)`,
          value: data.total,
          extra: 0,
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 12);

      // ── Services les plus visités PAR SOURCE (filtre Google / WhatsApp…) ──
      const serviceSources: SourceItem[] = Array.from(themeServiceBySource.keys())
        .map((name) => ({
          name,
          value: Array.from(themeServiceBySource.get(name)!.values())
            .reduce((sum, e) => sum + e.total, 0),
        }))
        .sort((a, b) => b.value - a.value);
      const topServicesBySource: Record<string, RankItem[]> = {};
      themeServiceBySource.forEach((bySrc, srcName) => {
        topServicesBySource[srcName] = Array.from(bySrc.entries())
          .map(([id, data]) => ({
            id: String(id),
            title: serviceNameById.get(id) || annonceNameById.get(id) || `Service #${id}`,
            sub: `${data.ips.size} unique(s)`,
            value: data.total,
            extra: 0,
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 12);
      });

      // ── Derniers visiteurs ─────────────────────────────────
      const recentVisits: RecentVisit[] = (allRows as (VisitRow & { __kind: string })[])
        .filter((row) => row.date)
        .sort((a, b) => String(b.date).localeCompare(String(a.date)))
        .slice(0, 12)
        .map((row, index) => ({
          id: `${row.__kind}-${row.id}-${index}`,
          kind: KIND_BY_TABLE[row.__kind] || 'Visite',
          boutique: row.boutique_nom || row.boutique
            || commerceNameById.get(row.boutique_id ?? 0)
            || 'Accueil Zifek',
          ip: maskIp(row.ip),
          date: String(row.date),
          source: row.source,
        }));

      // ── Géolocalisation (pays / villes) ────────────────────
      let geo = await loadGeo(siteIds, startIso, endIso);
      if (geo.pending > 0 && !geoEnrichAttempted && !enrichRef.current) {
        enrichRef.current = true;
        geoEnrichAttempted = true;
        try {
          await supabase.functions.invoke('geo-locate', { body: {} });
          geo = await loadGeo(siteIds, startIso, endIso);
        } catch {
          /* enrichissement best-effort */
        }
      }

      // ── Traction (croissance 7j/30j + taux de retour) ──────
      const traction = await loadTraction(siteIds);

      setStats({
        visitsAllTime: siteAll + prodAll + servAll + landAll,
        uniqueAllTime: Math.min(allIps.size, siteAll + prodAll + servAll + landAll),
        visitsPeriod: siteRows.length + prodRows.length + servRows.length + landRows.length
          + themeKindCount.product + themeKindCount.service + themeKindCount.booking
          + themeKindCount.form + themeKindCount.hebergement,
        uniquePeriod: Math.min(periodIps.size, siteRows.length + prodRows.length + servRows.length + landRows.length),
        landingVisits: landRows.length,
        siteVisits: siteRows.length,
        productVisits: prodRows.length + themeKindCount.product,
        serviceVisits: servRows.length + themeKindCount.service,
        bookingVisits: themeKindCount.booking,
        formVisits: themeKindCount.form,
        hebergementVisits: themeKindCount.hebergement,
        bookings,
        serviceConversion: (servRows.length + themeKindCount.service) > 0
          ? (bookings / (servRows.length + themeKindCount.service)) * 100
          : null,
        productConversion: (prodRows.length + themeKindCount.product) > 0
          ? (orderRows.length / (prodRows.length + themeKindCount.product)) * 100
          : null,
        siteUsers: userWebsiteRows.length,
        sites: sitesCount,
        products: prodCount,
        services: servCount,
        orders: orderRows.length,
        revenue,
        currency,
        visitsByDay,
        sources,
        topSites,
        topProducts,
        topProductsBySource,
        productSources,
        topServices,
        topServicesBySource,
        serviceSources,
        recentVisits,
        geoCountries: geo.countries,
        geoCities: geo.cities,
        geoTotal: geo.total,
        geoPending: geo.pending,
        comparisonByDay,
        marketplace,
        accountTypes,
        acquisition,
        sourceEvolution,
        traction,
        deltas: {
          visits: pctChange(siteRows.length + prodRows.length + servRows.length + landRows.length, prevVisits),
          unique: pctChange(periodIps.size, prevIps.size),
          orders: pctChange(orderRows.length, prevOrderRows.length),
          revenue: pctChange(revenue, prevRevenue),
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement des statistiques');
      setStats(EMPTY_STATS);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, scopeId, accountType, commerces]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, retry: fetchStats };
}

// ─────────────────────────────────────────────────────────────
// Liste des commerces / sites (pour le filtre en haut de page)
// ─────────────────────────────────────────────────────────────
export interface CommerceListResult {
  commerces: CommerceOption[];
  loading: boolean;
}

export function useCommerceList(): CommerceListResult {
  const [commerces, setCommerces] = useState<CommerceOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [usersRes, typesRes, clientTypesRes] = await Promise.all([
          supabase
            .from('users')
            .select('id, user_name, nomcommerce, typecompte')
            .gt('typecompte', 1)
            .order('user_name', { ascending: true })
            .limit(1000),
          supabase.from('typecompte').select('id, nom'),
          supabase.from('typecompte').select('id').eq('statustypecompte', 1),
        ]);
        const typeNameById = new Map<number, string>();
        ((typesRes.data || []) as { id: number; nom: string | null }[]).forEach((t) => {
          typeNameById.set(t.id, t.nom || '');
        });
        const clientTypeIds = new Set<number>(
          ((clientTypesRes.data || []) as { id: number }[]).map((t) => t.id),
        );
        const list: CommerceOption[] = ((usersRes.data || []) as {
          id: number;
          user_name: string | null;
          nomcommerce: string | null;
          typecompte: number | null;
        }[])
          .filter((u) => u.typecompte == null || !clientTypeIds.has(u.typecompte))
          .map((u) => ({
            id: u.id,
            name: u.nomcommerce || u.user_name || `Commerce #${u.id}`,
            typeName: u.typecompte != null ? typeNameById.get(u.typecompte) || null : null,
          }));
        if (active) setCommerces(list);
      } catch {
        if (active) setCommerces([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  return { commerces, loading };
}